import type { CSSProperties } from 'react';

export type PowerPstyleThemeKind = 'base' | 'bonus';

/** Dados mínimos de um power para secção / tema Pstyle (sem importar powers-data). */
export type PowerPstyleSectionSource = {
  slug: string;
  name: string;
  description: { short: string; long: string };
  smilies?: string[];
  pstyle?: PowerPstyleCmsConfig;
};

/** Nome e ID fixos na pré-visualização do diálogo. */
export const PSTYLE_DIALOG_DISPLAY_NAME = 'xatAjuda';
export const PSTYLE_DIALOG_USER_ID = '123456789';
export const PSTYLE_DIALOG_ONLINE_STATUS = 'Online';
export const PSTYLE_DIALOG_FRIEND_STATUS = '(Não adicionou você como amigo)';

export type PowerPstyleButtonPattern = 'none' | 'spiderweb';

/** Texto da coluna esquerda da secção pública Pstyle (editável no painel). */
export type PowerPstyleSectionCopy = {
  title: string;
  requirement: string;
  intro: string;
  body: string;
  tip: string;
};

/** Configuração Pstyle editável no painel CMS. */
export type PowerPstyleCmsConfig = {
  gradient: string;
  overlayImageUrl: string;
  overlayOpacity: number;
  overlayBackgroundSize: string;
  overlayBackgroundPosition: string;
  infoBoxBackground: string;
  statusText: string;
  statusTextColor: string;
  avatarCode: string;
  buttonPattern: PowerPstyleButtonPattern;
  /** Conteúdo textual da página pública (coluna esquerda). */
  sectionCopy?: PowerPstyleSectionCopy;
};

/** Fundo temático Pstyle — degradê nos botões + imagem sobreposta (ex.: Astrox). */
export type PowerPstyleThemeBackground = {
  gradient: string;
  overlayImageUrl: string;
  overlayOpacity?: number;
  /** Escala da imagem nos botões (maior = estrelas mais distantes). */
  overlayBackgroundSize?: string;
  overlayBackgroundPosition?: string;
};

export type PowerPstyleDialogMock = {
  headerGradient: string;
  /** Cor do texto no header (ex.: tema Stella). */
  headerTextColor?: string;
  bodyBackground: string;
  /** Degradê + imagem apenas nos botões do diálogo (header usa só headerGradient). */
  themeBackground?: PowerPstyleThemeBackground;
  infoBoxBackground: string;
  infoBoxTextColor: string;
  statusBarBackground: string;
  statusBarBorder: string;
  statusTextColor: string;
  buttonGradient: string;
  buttonPattern?: PowerPstyleButtonPattern;
  /** Texto dos botões de ação (ex.: tema Stella usa #010101). */
  buttonTextColor?: string;
  /** Glow/sombra nas caixas de nome e estado online (ex.: Sunburst). */
  infoBoxShadow?: string;
  /** Sombra ou contorno nos botões de ação (ex.: Sunburst). */
  buttonBoxShadow?: string;
  avatarCode: string;
  displayName: string;
  userId?: string;
  statusText: string;
  onlineStatus?: string;
  friendStatus?: string;
  /** Degradê animado xat (`.pg9` / `.pg10` + `.pstyleFx`) — header e botões. */
  animatedGradient?: string;
};

export type PowerPstyleTheme = {
  kind: PowerPstyleThemeKind;
  themeName: string;
  requirement?: string;
  previewImageUrl?: string;
  mock?: PowerPstyleDialogMock;
};

/** Cores Morgana extraídas do diálogo real do xat (referência xatimg). */
export const MORGANA_PSTYLE = {
  headerGradient: 'linear-gradient(90deg, #2b2e83 0%, #4e43ab 100%)',
  infoBoxBackground: 'rgba(140, 142, 199, 0.72)',
  infoBoxTextColor: '#ffffff',
  buttonGradient: 'linear-gradient(180deg, #4e43ab 0%, #2b2e83 100%)',
  statusTextColor: '#333366',
  statusBarBorder: '#c8cad4',
} as const;

/** Astrox — classes xat: `.pg16` + `.psAstrox:before`. */
export const ASTROX_PSTYLE = {
  themeGradient: 'linear-gradient(225deg, #633775, #361e40)',
  overlayImageUrl: 'https://xat.com/content/web/R00245/box/www/img/astrox.png',
  overlayOpacity: 0.35,
  /** ~175% do cover — estrelas mais pequenas/distantes, posição centrada como no xat. */
  overlayBackgroundSize: '280px auto',
  overlayBackgroundPosition: '50% 50%',
  headerGradient: 'linear-gradient(225deg, #633775, #361e40)',
  infoBoxBackground: 'rgba(120, 72, 140, 0.72)',
  infoBoxTextColor: '#ffffff',
  buttonGradient: 'linear-gradient(180deg, rgba(80, 45, 95, 0.92), rgba(40, 20, 48, 0.95))',
  statusTextColor: '#361e40',
  statusBarBorder: '#c8cad4',
} as const;

const XAT_PSTYLE_IMG = 'https://xat.com/content/web/R00245/box/www/img';

/** Kitsune — `.pg14` + `.psKitsune:before` (pstyle.css do xat; texto escuro). */
export const KITSUNE_PSTYLE = {
  themeGradient: 'linear-gradient(225deg, #e9e7e7, #ffffff)',
  overlayImageUrl: `${XAT_PSTYLE_IMG}/kitsune.png`,
  overlayOpacity: 0.18,
  overlayBackgroundSize: '240px auto',
  overlayBackgroundPosition: '50% 50%',
  headerGradient: 'linear-gradient(225deg, #e9e7e7, #ffffff)',
  infoBoxBackground: 'rgba(233, 231, 231, 0.95)',
  infoBoxTextColor: '#010101',
  statusTextColor: '#010101',
  statusBarBorder: '#c8cad4',
} as const;

/** Lems — `.pg13` + `.psLems:before`. */
export const LEMS_PSTYLE = {
  themeGradient: 'linear-gradient(225deg, #fdb9c8, #fc87a1)',
  overlayImageUrl: `${XAT_PSTYLE_IMG}/lems.png`,
  overlayOpacity: 0.2,
  overlayBackgroundSize: '240px auto',
  overlayBackgroundPosition: '50% 50%',
  headerGradient: 'linear-gradient(225deg, #fdb9c8, #fc87a1)',
  infoBoxBackground: 'rgba(252, 135, 161, 0.82)',
  infoBoxTextColor: '#ffffff',
  statusTextColor: '#9a3d55',
  statusBarBorder: '#c8cad4',
} as const;

/** Stella — `.pg15` + `.psStella:before`. */
export const STELLA_PSTYLE = {
  themeGradient: 'linear-gradient(90deg, #e2a2bd, #c996dc)',
  overlayImageUrl: `${XAT_PSTYLE_IMG}/stella.png`,
  overlayOpacity: 0.35,
  overlayBackgroundSize: 'cover',
  overlayBackgroundPosition: '50% 50%',
  headerGradient: 'linear-gradient(90deg, #e2a2bd, #c996dc)',
  infoBoxBackground: 'rgba(201, 150, 220, 0.78)',
  infoBoxTextColor: '#ffffff',
  statusTextColor: '#5c3d6b',
  statusBarBorder: '#c8cad4',
} as const;

/** Cogu (Mushroom) — `.pg8` + `.mushRoom:before`. */
export const COGU_PSTYLE = {
  themeGradient: 'linear-gradient(225deg, #ff2a1c, #ff6f64)',
  overlayImageUrl: `${XAT_PSTYLE_IMG}/mush.png`,
  overlayOpacity: 0.2,
  overlayBackgroundSize: 'cover',
  overlayBackgroundPosition: '50% 50%',
  headerGradient: 'linear-gradient(225deg, #ff2a1c, #ff6f64)',
  infoBoxBackground: 'rgba(255, 90, 80, 0.85)',
  infoBoxTextColor: '#ffffff',
  statusTextColor: '#8b1a12',
  statusBarBorder: '#c8cad4',
} as const;

/** Horrorgal (Pumpkin) — `.pg11` + `.psHalloween:before` (pstyle.css do xat). */
export const HORRORGAL_PSTYLE = {
  themeGradient: 'linear-gradient(147deg, #c34222, #fdbb2d)',
  overlayImageUrl: `${XAT_PSTYLE_IMG}/halloween.png`,
  overlayOpacity: 0.2,
  overlayBackgroundSize: '20em',
  overlayBackgroundPosition: '50% 50%',
  headerGradient: 'linear-gradient(147deg, #c34222, #fdbb2d)',
  infoBoxBackground: 'rgba(195, 66, 34, 0.85)',
  infoBoxTextColor: '#ffffff',
  statusTextColor: '#8b2e12',
  statusBarBorder: '#c8cad4',
} as const;

/** Temas bónus documentados na wiki + power Pstyle base. */
export const POWER_PSTYLE_THEMES: Record<string, PowerPstyleTheme> = {
  pstyle: {
    kind: 'base',
    themeName: 'Pstyle',
    mock: {
      headerGradient: 'linear-gradient(90deg, #0c4a6e 0%, #0369a1 100%)',
      bodyBackground: '#ffffff',
      infoBoxBackground: 'rgba(56, 189, 248, 0.9)',
      infoBoxTextColor: '#ffffff',
      statusBarBackground: '#ffffff',
      statusBarBorder: '#c8cad4',
      statusTextColor: '#0c4a6e',
      buttonGradient: 'linear-gradient(180deg, #0284c7 0%, #0c4a6e 100%)',
      buttonPattern: 'none',
      avatarCode: 'pstyle',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'hello from xat',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
  morgana: {
    kind: 'bonus',
    themeName: 'Morgana',
    requirement: 'Power Pstyle + Morgana',
    mock: {
      headerGradient: MORGANA_PSTYLE.headerGradient,
      bodyBackground: '#ffffff',
      infoBoxBackground: MORGANA_PSTYLE.infoBoxBackground,
      infoBoxTextColor: MORGANA_PSTYLE.infoBoxTextColor,
      statusBarBackground: '#ffffff',
      statusBarBorder: MORGANA_PSTYLE.statusBarBorder,
      statusTextColor: MORGANA_PSTYLE.statusTextColor,
      buttonGradient: MORGANA_PSTYLE.buttonGradient,
      buttonPattern: 'spiderweb',
      avatarCode: 'morgana',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'trick or xat',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
  horrorgal: {
    kind: 'bonus',
    themeName: 'Horrorgal',
    requirement: 'Power Pstyle + Horrorgal',
    mock: {
      headerGradient: HORRORGAL_PSTYLE.headerGradient,
      bodyBackground: '#ffffff',
      themeBackground: {
        gradient: HORRORGAL_PSTYLE.themeGradient,
        overlayImageUrl: HORRORGAL_PSTYLE.overlayImageUrl,
        overlayOpacity: HORRORGAL_PSTYLE.overlayOpacity,
        overlayBackgroundSize: HORRORGAL_PSTYLE.overlayBackgroundSize,
        overlayBackgroundPosition: HORRORGAL_PSTYLE.overlayBackgroundPosition,
      },
      infoBoxBackground: HORRORGAL_PSTYLE.infoBoxBackground,
      infoBoxTextColor: HORRORGAL_PSTYLE.infoBoxTextColor,
      statusBarBackground: '#ffffff',
      statusBarBorder: HORRORGAL_PSTYLE.statusBarBorder,
      statusTextColor: HORRORGAL_PSTYLE.statusTextColor,
      buttonGradient: HORRORGAL_PSTYLE.themeGradient,
      avatarCode: 'horrorgal',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'they found you',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
  astrox: {
    kind: 'bonus',
    themeName: 'Astrox',
    requirement: 'Power Pstyle + Astrox',
    mock: {
      headerGradient: ASTROX_PSTYLE.headerGradient,
      bodyBackground: '#ffffff',
      themeBackground: {
        gradient: ASTROX_PSTYLE.themeGradient,
        overlayImageUrl: ASTROX_PSTYLE.overlayImageUrl,
        overlayOpacity: ASTROX_PSTYLE.overlayOpacity,
        overlayBackgroundSize: ASTROX_PSTYLE.overlayBackgroundSize,
        overlayBackgroundPosition: ASTROX_PSTYLE.overlayBackgroundPosition,
      },
      infoBoxBackground: ASTROX_PSTYLE.infoBoxBackground,
      infoBoxTextColor: ASTROX_PSTYLE.infoBoxTextColor,
      statusBarBackground: '#ffffff',
      statusBarBorder: ASTROX_PSTYLE.statusBarBorder,
      statusTextColor: ASTROX_PSTYLE.statusTextColor,
      buttonGradient: ASTROX_PSTYLE.buttonGradient,
      avatarCode: 'astshine',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'lost in space',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
  kitsune: {
    kind: 'bonus',
    themeName: 'Kitsune',
    requirement: 'Power Pstyle + Kitsune',
    mock: {
      headerGradient: KITSUNE_PSTYLE.headerGradient,
      headerTextColor: '#010101',
      bodyBackground: '#ffffff',
      themeBackground: {
        gradient: KITSUNE_PSTYLE.themeGradient,
        overlayImageUrl: KITSUNE_PSTYLE.overlayImageUrl,
        overlayOpacity: KITSUNE_PSTYLE.overlayOpacity,
        overlayBackgroundSize: KITSUNE_PSTYLE.overlayBackgroundSize,
        overlayBackgroundPosition: KITSUNE_PSTYLE.overlayBackgroundPosition,
      },
      infoBoxBackground: KITSUNE_PSTYLE.infoBoxBackground,
      infoBoxTextColor: KITSUNE_PSTYLE.infoBoxTextColor,
      statusBarBackground: '#ffffff',
      statusBarBorder: KITSUNE_PSTYLE.statusBarBorder,
      statusTextColor: KITSUNE_PSTYLE.statusTextColor,
      buttonGradient: KITSUNE_PSTYLE.themeGradient,
      buttonTextColor: '#010101',
      avatarCode: 'kitsune',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'kitsune spirit',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
  lems: {
    kind: 'bonus',
    themeName: 'Lems',
    requirement: 'Power Pstyle + Lems',
    mock: {
      headerGradient: LEMS_PSTYLE.headerGradient,
      bodyBackground: '#ffffff',
      themeBackground: {
        gradient: LEMS_PSTYLE.themeGradient,
        overlayImageUrl: LEMS_PSTYLE.overlayImageUrl,
        overlayOpacity: LEMS_PSTYLE.overlayOpacity,
        overlayBackgroundSize: LEMS_PSTYLE.overlayBackgroundSize,
        overlayBackgroundPosition: LEMS_PSTYLE.overlayBackgroundPosition,
      },
      infoBoxBackground: LEMS_PSTYLE.infoBoxBackground,
      infoBoxTextColor: LEMS_PSTYLE.infoBoxTextColor,
      statusBarBackground: '#ffffff',
      statusBarBorder: LEMS_PSTYLE.statusBarBorder,
      statusTextColor: LEMS_PSTYLE.statusTextColor,
      buttonGradient: LEMS_PSTYLE.themeGradient,
      avatarCode: 'lems',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'sip sip!',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
  stella: {
    kind: 'bonus',
    themeName: 'Stella',
    requirement: 'Power Pstyle + Stella',
    mock: {
      headerGradient: STELLA_PSTYLE.headerGradient,
      bodyBackground: '#ffffff',
      themeBackground: {
        gradient: STELLA_PSTYLE.themeGradient,
        overlayImageUrl: STELLA_PSTYLE.overlayImageUrl,
        overlayOpacity: STELLA_PSTYLE.overlayOpacity,
        overlayBackgroundSize: STELLA_PSTYLE.overlayBackgroundSize,
        overlayBackgroundPosition: STELLA_PSTYLE.overlayBackgroundPosition,
      },
      infoBoxBackground: STELLA_PSTYLE.infoBoxBackground,
      infoBoxTextColor: STELLA_PSTYLE.infoBoxTextColor,
      statusBarBackground: '#ffffff',
      statusBarBorder: STELLA_PSTYLE.statusBarBorder,
      statusTextColor: STELLA_PSTYLE.statusTextColor,
      buttonGradient: STELLA_PSTYLE.themeGradient,
      avatarCode: 'stellamoonb',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'stellar charm',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
  cogu: {
    kind: 'bonus',
    themeName: 'Mushroom',
    requirement: 'Power Pstyle + Cogu',
    mock: {
      headerGradient: COGU_PSTYLE.headerGradient,
      bodyBackground: '#ffffff',
      themeBackground: {
        gradient: COGU_PSTYLE.themeGradient,
        overlayImageUrl: COGU_PSTYLE.overlayImageUrl,
        overlayOpacity: COGU_PSTYLE.overlayOpacity,
        overlayBackgroundSize: COGU_PSTYLE.overlayBackgroundSize,
        overlayBackgroundPosition: COGU_PSTYLE.overlayBackgroundPosition,
      },
      infoBoxBackground: COGU_PSTYLE.infoBoxBackground,
      infoBoxTextColor: COGU_PSTYLE.infoBoxTextColor,
      statusBarBackground: '#ffffff',
      statusBarBorder: COGU_PSTYLE.statusBarBorder,
      statusTextColor: COGU_PSTYLE.statusTextColor,
      buttonGradient: COGU_PSTYLE.themeGradient,
      avatarCode: 'cogu',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'fungi party',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
  yellow: {
    kind: 'bonus',
    themeName: 'Sunburst',
    requirement: 'Power Pstyle + Yellow',
    mock: {
      headerGradient: '#FFB800',
      headerTextColor: '#ffffff',
      bodyBackground: '#F9F9F9',
      infoBoxBackground: '#FFC800',
      infoBoxTextColor: '#ffffff',
      infoBoxShadow:
        '0 0 0 1px rgba(255, 255, 255, 0.65), 0 0 6px rgba(255, 255, 255, 0.55)',
      statusBarBackground: '#ffffff',
      statusBarBorder: '#c8cad4',
      statusTextColor: '#92400e',
      buttonGradient: 'linear-gradient(90deg, #FFD700 0%, #FF9500 100%)',
      buttonTextColor: '#ffffff',
      buttonBoxShadow:
        'inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 1px 0 rgba(153, 76, 0, 0.55)',
      avatarCode: 'yellow',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'sunburst vibes',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  },
};

/** Emerald Fx — `.pg9` + `.pstyleFx` (pstyle.css do xat). */
export const EMERALD_FX_PSTYLE_GRADIENT =
  'linear-gradient(-45deg,#006105,#16921b,#021401,#006105,#09260a,#168a09,#065200)';

/** Supernova Fx — `.pg10` + `.pstyleFx` (pstyle.css do xat). */
export const SUPERNOVA_FX_PSTYLE_GRADIENT =
  'linear-gradient(-45deg,#ee7752,#e73c7e,#23a6d5,#23d5ab)';

/** Estilo completo para degradê animado (evita `background` shorthand que zera background-size). */
export function pstyleAnimatedGradientStyle(gradient: string): CSSProperties {
  return {
    backgroundImage: gradient,
    backgroundSize: '400% 400%',
    backgroundRepeat: 'no-repeat',
    animation: 'pstyle-fx 15s ease infinite',
  };
}

export type EverypowerPstyleVariantId = 'emerald-fx' | 'supernova-fx';

export type EverypowerPstyleVariant = {
  id: EverypowerPstyleVariantId;
  themeName: string;
  wikiPreviewUrl: string;
  mock: PowerPstyleDialogMock;
};

export const EVERYPOWER_PSTYLE_VARIANTS: EverypowerPstyleVariant[] = [
  {
    id: 'emerald-fx',
    themeName: 'Emerald Fx',
    wikiPreviewUrl: 'https://wiki.xat.com/w/images/f/f5/Emerald_Fx.gif',
    mock: {
      animatedGradient: EMERALD_FX_PSTYLE_GRADIENT,
      headerGradient: EMERALD_FX_PSTYLE_GRADIENT,
      bodyBackground: '#ffffff',
      infoBoxBackground: 'rgba(22, 146, 27, 0.78)',
      infoBoxTextColor: '#ffffff',
      statusBarBackground: '#ffffff',
      statusBarBorder: '#b8d4b9',
      statusTextColor: '#021401',
      buttonGradient: EMERALD_FX_PSTYLE_GRADIENT,
      avatarCode: 'everypower',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'emerald everypower',
      onlineStatus: 'Online Proprietário',
      friendStatus: 'Amigo mútuo',
    },
  },
  {
    id: 'supernova-fx',
    themeName: 'Supernova Fx',
    wikiPreviewUrl: 'https://wiki.xat.com/w/images/1/1c/SupernovaFx.gif',
    mock: {
      animatedGradient: SUPERNOVA_FX_PSTYLE_GRADIENT,
      headerGradient: SUPERNOVA_FX_PSTYLE_GRADIENT,
      bodyBackground: '#ffffff',
      infoBoxBackground: 'rgba(231, 60, 126, 0.78)',
      infoBoxTextColor: '#ffffff',
      statusBarBackground: '#ffffff',
      statusBarBorder: '#f0c4d4',
      statusTextColor: '#9a2d5c',
      buttonGradient: SUPERNOVA_FX_PSTYLE_GRADIENT,
      avatarCode: 'everypower',
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'supernova everypower',
      onlineStatus: 'Online Proprietário',
      friendStatus: 'Amigo mútuo',
    },
  },
];

export const PSTYLE_DEFAULT_GRADIENTS = [
  'Aegis',
  'Aurora',
  'Galaxy',
  'Neptune',
  'Ocean',
  'Phoenix',
] as const;

export const PSTYLE_BONUS_GRADIENTS = [
  { name: 'Kitsune', requirement: 'Kitsune' },
  { name: 'Lems', requirement: 'Lems' },
  { name: 'Morgana', requirement: 'Morgana' },
  { name: 'Stella', requirement: 'Stella' },
  { name: 'Sunburst', requirement: 'Yellow' },
  { name: 'Mushroom', requirement: 'Cogu' },
  { name: 'Astrox', requirement: 'Astrox' },
  { name: 'Horrorgal', requirement: 'Horrorgal' },
  { name: 'Emerald Fx', requirement: 'EveryPower' },
  { name: 'Supernova Fx', requirement: 'EveryPower' },
] as const;

export function getPowerPstyleTheme(slug: string): PowerPstyleTheme | null {
  return POWER_PSTYLE_THEMES[slug.toLowerCase()] ?? null;
}

export function powerShowsPstyleSection(power: PowerPstyleSectionSource): boolean {
  if (power.slug === 'everypower') return false;
  if (power.slug === 'pstyle') return true;
  if (getPowerPstyleTheme(power.slug)) return true;
  const text = `${power.description.short} ${power.description.long}`.toLowerCase();
  return /pstyle|profile theme|tema de perfil|tema.*perfil/.test(text);
}

export function resolvePowerPstyleTheme(power: PowerPstyleSectionSource): PowerPstyleTheme {
  const explicit = getPowerPstyleTheme(power.slug);
  if (explicit) return explicit;

  return {
    kind: 'bonus',
    themeName: power.name,
    requirement: `Power Pstyle + ${power.name}`,
    mock: {
      headerGradient: 'linear-gradient(90deg, #0a1450 0%, #1e3a8a 100%)',
      bodyBackground: '#ffffff',
      infoBoxBackground: 'rgba(59, 130, 246, 0.88)',
      infoBoxTextColor: '#ffffff',
      statusBarBackground: '#ffffff',
      statusBarBorder: '#c8cad4',
      statusTextColor: '#0a1450',
      buttonGradient: 'linear-gradient(180deg, #1d4ed8 0%, #0a1450 100%)',
      avatarCode: power.smilies?.[0] ?? power.slug,
      displayName: PSTYLE_DIALOG_DISPLAY_NAME,
      userId: PSTYLE_DIALOG_USER_ID,
      statusText: 'online on xat',
      onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
      friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
    },
  };
}

const DEFAULT_PSTYLE_INTRO =
  'O Pstyle personaliza o diálogo de perfil no chat do xat — fundo, cores, gradientes, ícones e botões de ação. Não altera o nick no chat.';

const DEFAULT_PSTYLE_BODY_BASE =
  'Com o power Pstyle, abra Configurações → Pstyle para editar a imagem ou cor de fundo, a cor das caixas de informação, escolher um gradiente e ocultar ícones do perfil (Registrado, Casado/BFF, Homepage, Presentes, etc.).';

const DEFAULT_PSTYLE_TIP =
  'Clique no seu nome na lista de utilizadores → Configurações → Pstyle. Use Pré-visualizar para ver o diálogo antes de guardar. Nota: o Pstyle não aparece no Modo Noite.';

export function defaultPstyleSectionCopy(
  power: Pick<PowerPstyleSectionSource, 'name' | 'slug'>,
  theme?: PowerPstyleTheme | null
): PowerPstyleSectionCopy {
  const resolved = theme ?? getPowerPstyleTheme(power.slug);
  const isBase = resolved?.kind === 'base' || power.slug === 'pstyle';
  const themeName = resolved?.themeName ?? power.name;
  const body = isBase
    ? DEFAULT_PSTYLE_BODY_BASE
    : `O power ${power.name} desbloqueia um tema bónus no separador Pstyle. É necessário ter o power Pstyle activo para aplicar temas adicionais.`;

  return {
    title: isBase ? 'Pstyle' : `Tema Pstyle · ${themeName}`,
    requirement: resolved?.requirement ?? (isBase ? '' : `Power Pstyle + ${power.name}`),
    intro: `${DEFAULT_PSTYLE_INTRO}\n\n${body}`,
    body: '',
    tip: DEFAULT_PSTYLE_TIP,
  };
}

export function normalizePstyleSectionCopy(
  raw: Partial<PowerPstyleSectionCopy> | null | undefined,
  fallback: PowerPstyleSectionCopy
): PowerPstyleSectionCopy {
  const introRaw = typeof raw?.intro === 'string' ? raw.intro : fallback.intro;
  const bodyRaw = typeof raw?.body === 'string' ? raw.body.trim() : '';
  const intro =
    bodyRaw && !introRaw.includes(bodyRaw)
      ? [introRaw.trim(), bodyRaw].filter(Boolean).join('\n\n')
      : introRaw.trim() || fallback.intro;

  return {
    title: typeof raw?.title === 'string' ? raw.title : fallback.title,
    requirement: typeof raw?.requirement === 'string' ? raw.requirement : fallback.requirement,
    intro,
    body: '',
    tip: typeof raw?.tip === 'string' ? raw.tip : fallback.tip,
  };
}
